/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useAppSelector } from "../../redux/hooks";
import { selectSelectedLeague } from "../../redux/slice/selectedLeagueSlice";
import { canManageDivisionsInLeague } from "../../utils/leagueAccess";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { fetchDivisions } from "../myTeam/api/myTeamApi";
import { extractDivisionListFromPayload, normalizeDivisionList } from "../leaguesHome/teams/divisionTeamUtils";
import SetDivisionsGroupModal from "../leaguesHome/teams/SetDivisionsGroupModal";
import UpdateTeamModal from "../leaguesHome/teams/teamInfo/UpdateTeamModal";
import { getAuthToken, getPlayerImageUrl } from "../../helper/helper";
import { IcArr, IcCalL, IcDn, IcGrid, IcList, IcSearch, IcSrchL, IcTick } from "../tournamentPage/icons";
import { norm, recordFromMatches, resultPill, teamPath, useEditableTeams, useLiveTeams, useTeamRecords, useTournamentTeams, type TeamItem, type TeamRecord } from "./teamsData";
import { useTournamentMatches } from "../tournamentPage/useTournamentMatches";
import { buildAllVms } from "../tournamentPage/matchVm";
import "./teamsTab.scss";

type Props = { tournamentId: string; tournamentName: string; tournamentStart?: string | null };

export const TeamCrest = ({ t, size }: { t: Pick<TeamItem, "name" | "code" | "color" | "image">; size?: number }) => {
  const [bad, setBad] = useState(false);
  const url = getPlayerImageUrl(t.image) ?? "";
  return (
    <span className="tcrest" style={{ background: t.color, width: size, height: size, fontSize: size ? Math.max(9, size / 3.6) : undefined }} aria-hidden="true">
      {url && !bad ? <img src={url} alt="" onError={() => setBad(true)} /> : t.code}
    </span>
  );
};

export const FormPills = ({ form, big }: { form: string[]; big?: boolean }) => (
  <span className={`fpills${big ? " big" : ""}`}>
    {form.slice(0, 5).map((r, i) => {
      const p = resultPill(r);
      return <b key={i} className={`fp ${p}`}>{p}</b>;
    })}
  </span>
);

const emptyRecord = (t: TeamItem): TeamRecord => ({ played: t.counts.won + t.counts.lost + t.counts.tied, won: t.counts.won, lost: t.counts.lost, tied: t.counts.tied, nr: 0, form: [] });

const Skeleton = () => (
  <div className="tgrid" aria-hidden="true">
    {Array.from({ length: 8 }, (_, i) => (
      <div className="tcard skel" key={i}>
        <div className="tc-top"><span className="tcrest sk" /><span><i className="sk l1" /><i className="sk l2" /></span></div>
        <i className="sk blk" /><i className="sk l3" />
      </div>
    ))}
  </div>
);

const Empty = ({ icon, title, text, acts, meta }: { icon: React.ReactNode; title: string; text: string; acts?: React.ReactNode; meta?: string }) => (
  <div className="empty"><span className="ic">{icon}</span><h3>{title}</h3><p>{text}</p>{meta && <span className="meta2">{meta}</span>}{acts && <div className="acts">{acts}</div>}</div>
);

const TeamsTab = ({ tournamentId, tournamentName, tournamentStart }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const canManage = canManageDivisionsInLeague(selectedLeague);

  const teamsQ = useTournamentTeams(tournamentId);
  const divisions = teamsQ.data?.divisions ?? [];
  const teams = teamsQ.data?.teams ?? [];
  const records = useTeamRecords(useMemo(() => divisions.map((d) => d.id), [divisions]));
  const live = useLiveTeams(tournamentId).data;
  // league admins can edit every team; anyone else (team admin / captain / vice-captain) only their own squads
  const leagueAdmin = (selectedLeague?.roleName ?? "").toUpperCase() === "LEAGUE_ADMIN";
  const editable = useEditableTeams(useMemo(() => teams.map((t) => t.snapshotId), [teams]), !leagueAdmin && !!getAuthToken());
  const canEdit = (t: TeamItem) => !!t.snapshotId && (leagueAdmin || t.canEdit || editable.has(t.snapshotId));
  const all = useTournamentMatches(tournamentId);
  const vms = useMemo(() => buildAllVms(all.live, all.up, all.past), [all.live, all.up, all.past]);

  const [q, setQ] = useState("");
  const [divId, setDivId] = useState("all");
  const [groupId, setGroupId] = useState("all");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [menu, setMenu] = useState<"div" | "group" | null>(null);
  const [showDivModal, setShowDivModal] = useState(false);
  const [edit, setEdit] = useState<{ open: boolean; teamId: string; snapshotId: string; name: string }>({ open: false, teamId: "", snapshotId: "", name: "" });
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = () => setMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const activeDiv = divisions.find((d) => d.id === divId);
  const hasGroups = !!activeDiv && activeDiv.groups.length > 0;
  // finished matches first (agrees with the team page), points table while they load
  const recordOf = (t: TeamItem) => recordFromMatches(vms, t.snapshotId, t.name) ?? records.byId.get(t.snapshotId) ?? records.byName.get(norm(t.name)) ?? emptyRecord(t);
  const isLive = (t: TeamItem) => !!live && (live.ids.has(t.snapshotId) || live.names.has(norm(t.name)));
  const anyPlayed = teams.some((t) => recordOf(t).played > 0);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return teams.filter((t) => {
      if (divId !== "all" && t.divisionId !== divId) return false;
      if (groupId !== "all" && t.groupId !== groupId) return false;
      return !ql || t.name.toLowerCase().includes(ql) || t.code.toLowerCase().includes(ql);
    });
  }, [teams, q, divId, groupId]);

  // sections: division, then group inside it; teams A to Z
  const sections = useMemo(() => {
    const out: { key: string; division: string; group: string; list: TeamItem[] }[] = [];
    divisions.forEach((d) => {
      const inDiv = filtered.filter((t) => t.divisionId === d.id);
      if (d.groups.length) {
        d.groups.forEach((g) => {
          const list = inDiv.filter((t) => t.groupId === g.id);
          if (list.length) out.push({ key: g.id, division: d.name, group: g.name, list });
        });
        const loose = inDiv.filter((t) => !t.groupId);
        if (loose.length) out.push({ key: `${d.id}-x`, division: d.name, group: "", list: loose });
      } else if (inDiv.length) out.push({ key: d.id, division: d.name, group: "", list: inDiv });
    });
    out.forEach((s) => s.list.sort((a, b) => a.name.localeCompare(b.name)));
    return out;
  }, [filtered, divisions]);

  const anyFilter = !!q.trim() || divId !== "all" || groupId !== "all";
  const clear = () => { setQ(""); setDivId("all"); setGroupId("all"); };
  const open = (t: TeamItem) => navigate(teamPath(tournamentId, t.snapshotId));
  const multiDiv = divisions.length > 1;

  const leagueIdForNav = () => selectedLeague?.leagueId || sessionStorage.getItem("leagueId") || "";
  const addPlayers = () => navigate("/tournaments/add-players", { state: { tournamentId, leagueId: leagueIdForNav() } });
  const editDivision = async () => {
    try {
      const payload = await queryClient.fetchQuery({ queryKey: [QUERY_KEY.getDivisions, tournamentId], queryFn: () => fetchDivisions(tournamentId) });
      const fresh = normalizeDivisionList(extractDivisionListFromPayload(payload));
      const lid = leagueIdForNav();
      navigate("/tournaments/create-division", { state: { mode: "edit", divisions: fresh, ...(lid ? { leagueId: lid } : {}) } });
    } catch {
      toast.error("Could not load divisions. Please try again.");
    }
  };
  const createDivisions = (numDivisions: number, numGroups: number) => {
    const lid = leagueIdForNav();
    navigate("/tournaments/create-division", { state: { mode: "create", numDivisions, numGroups, ...(lid ? { leagueId: lid } : {}) } });
  };
  const openEdit = (e: React.MouseEvent, t: TeamItem) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem("tournamentId", tournamentId); // UpdateTeamModal reads the tournament from here
    setEdit({ open: true, teamId: t.teamId, snapshotId: t.snapshotId, name: t.name });
  };

  const orgActs = canManage && (
    <span className="orgacts">
      <button type="button" className="btn" onClick={addPlayers}>+ Add Players</button>
      <button type="button" className="btn" onClick={() => void editDivision()}>Edit Division</button>
      <button type="button" className="btn primary" onClick={() => setShowDivModal(true)}>+ Create New Division</button>
    </span>
  );

  const card = (t: TeamItem) => {
    const r = recordOf(t);
    const lv = isLive(t);
    return (
      <a key={t.snapshotId || t.name} className="tcard" href={teamPath(tournamentId, t.snapshotId)} style={{ ["--tc" as any]: t.color }} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); open(t); }} aria-label={`${t.name}, open team page`}>
        <span className="tc-bar" />
        <div className="tc-top">
          <TeamCrest t={t} size={46} />
          <span className="tc-id">
            <b>{t.name}</b>
            {(t.captain || t.players != null) && <small>{[t.captain ? `Captain ${t.captain}` : "", t.players != null ? `${t.players} ${t.players === 1 ? "player" : "players"}` : ""].filter(Boolean).join(" · ")}</small>}
          </span>
          {canEdit(t) && <button type="button" className="tc-edit" aria-label={`Edit ${t.name}`} onClick={(e) => openEdit(e, t)}>✎</button>}
          <span className="tc-chev" aria-hidden="true"><IcArr /></span>
        </div>
        {lv && <div className="tc-flags"><span className="flag live"><i />LIVE NOW</span></div>}
        <div className="tc-rec">
          <span><small>PLAYED</small><b>{r.played}</b></span>
          <span className="w"><small>WON</small><b>{r.won}</b></span>
          <span><small>LOST</small><b>{r.lost}</b></span>
          <span><small>TIED</small><b>{r.tied}</b></span>
        </div>
        <div className="tc-form">{r.form.length ? <><span className="lb">LAST {Math.min(5, r.form.length)}</span><FormPills form={r.form} />{r.nr > 0 && <em>{r.nr} no result</em>}</> : <span className="none">Yet to play a match</span>}</div>
      </a>
    );
  };

  const row = (t: TeamItem) => {
    const r = recordOf(t);
    return (
      <a key={t.snapshotId || t.name} className="trow" href={teamPath(tournamentId, t.snapshotId)} onClick={(e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); open(t); }}>
        <span className="tr-team"><TeamCrest t={t} size={32} /><span><b>{t.name}</b>{(t.captain || isLive(t)) && <small>{t.captain ? `Captain ${t.captain}` : null}{t.captain && isLive(t) ? " · " : null}{isLive(t) ? <em className="lv">Live now</em> : null}</small>}</span></span>
        <span className="n">{r.played}</span><span className="n w">{r.won}</span><span className="n">{r.lost}</span><span className="n">{r.tied}</span>
        <span className="tr-form">{r.form.length ? <FormPills form={r.form} /> : <i className="none">Yet to play</i>}</span>
        <span className="tr-sq">{t.players == null ? "" : t.players === 0 ? "No players" : <><b>{t.players}</b> players</>}</span>
        {canEdit(t) ? <button type="button" className="tc-edit inline" aria-label={`Edit ${t.name}`} onClick={(e) => openEdit(e, t)}>✎</button> : <span />}
        <span className="tc-chev" aria-hidden="true"><IcArr /></span>
      </a>
    );
  };

  let body: React.ReactNode;
  if (teamsQ.isLoading) body = <Skeleton />;
  else if (teamsQ.isError)
    body = <Empty icon={<IcSrchL />} title="Couldn't load this" text="Check your connection and try again." acts={<button className="btn primary" onClick={() => void teamsQ.refetch()}>Try again</button>} />;
  else if (teams.length === 0)
    body = canManage ? (
      <Empty icon={<IcCalL />} title="Add the teams for this tournament" text="Create divisions and groups, then add teams and players to them." acts={<>{orgActs}</>} />
    ) : (
      <Empty icon={<IcCalL />} title="Teams haven't been announced yet" text={`The organiser is still adding teams for ${tournamentName}.`} meta={tournamentStart ? `Starts ${new Date(tournamentStart).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : undefined} />
    );
  else if (sections.length === 0)
    body = <Empty icon={<IcSrchL />} title={`No teams match${q.trim() ? ` “${q.trim()}”` : ""}${activeDiv ? ` in ${activeDiv.name}` : ""}`} text="Check the spelling, or search by team name or short code." acts={<button className="btn primary" onClick={clear}>Clear filters</button>} />;
  else
    body = sections.map((s) => (
      <section key={s.key} className="tsec">
        <div className="day"><h3>{s.group ? `${s.division} · ${s.group}` : multiDiv ? `${s.division}` : s.division}</h3><span>{s.list.length} {s.list.length === 1 ? "team" : "teams"}{anyPlayed ? " · A to Z" : " · No matches played yet"}</span></div>
        {view === "grid" ? <div className="tgrid">{s.list.map(card)}</div> : (
          <div className="tbl ttbl"><div className="th"><span>Team</span><span className="n">Played</span><span className="n">Won</span><span className="n">Lost</span><span className="n">Tied</span><span>Last 5</span><span>Squad</span><span /><span /></div>{s.list.map(row)}</div>
        )}
      </section>
    ));

  return (
    <div className="tp tm" ref={root}>
      <main className="wrap main">
        {!teamsQ.isLoading && !teamsQ.isError && teams.length > 0 && (
          <>
            <div className="tool">
              <label className="search" style={{ marginLeft: 0 }}><IcSearch /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams by name or code" aria-label="Search teams" />{q && <button className="clr" onClick={() => setQ("")} aria-label="Clear search">×</button>}</label>
              <div className="dd">
                <button className="ddb" aria-haspopup="listbox" aria-expanded={menu === "div"} onClick={(e) => { e.stopPropagation(); setMenu(menu === "div" ? null : "div"); }}>{activeDiv ? activeDiv.name : "All divisions"}<IcDn /></button>
                {menu === "div" && (
                  <div className="ddm" role="listbox" onClick={(e) => e.stopPropagation()}>
                    <button role="option" aria-selected={divId === "all"} className={divId === "all" ? "on" : ""} onClick={() => { setDivId("all"); setGroupId("all"); setMenu(null); }}>All divisions{divId === "all" && <span className="ck"><IcTick /></span>}</button>
                    {divisions.filter((d) => teams.some((t) => t.divisionId === d.id)).map((d) => (
                      <button key={d.id} role="option" aria-selected={divId === d.id} className={divId === d.id ? "on" : ""} onClick={() => { setDivId(d.id); setGroupId("all"); setMenu(null); }}>{d.name}<span className="m">{teams.filter((t) => t.divisionId === d.id).length} teams</span></button>
                    ))}
                    {divisions.length < 2 && <><hr /><div className="ddnote">This tournament has one division.</div></>}
                  </div>
                )}
              </div>
              {hasGroups && (
                <div className="dd">
                  <button className="ddb" aria-haspopup="listbox" aria-expanded={menu === "group"} onClick={(e) => { e.stopPropagation(); setMenu(menu === "group" ? null : "group"); }}>{activeDiv!.groups.find((g) => g.id === groupId)?.name ?? "All groups"}<IcDn /></button>
                  {menu === "group" && (
                    <div className="ddm" role="listbox" onClick={(e) => e.stopPropagation()}>
                      <button role="option" aria-selected={groupId === "all"} className={groupId === "all" ? "on" : ""} onClick={() => { setGroupId("all"); setMenu(null); }}>All groups{groupId === "all" && <span className="ck"><IcTick /></span>}</button>
                      {activeDiv!.groups.map((g) => (
                        <button key={g.id} role="option" aria-selected={groupId === g.id} className={groupId === g.id ? "on" : ""} onClick={() => { setGroupId(g.id); setMenu(null); }}>{g.name}<span className="m">{teams.filter((t) => t.groupId === g.id).length} teams</span></button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <span style={{ flex: 1 }} />
              {orgActs}
              <div className="view" role="group" aria-label="Layout">
                <button aria-pressed={view === "grid"} aria-label="Card view" onClick={() => setView("grid")}><IcGrid /></button>
                <button aria-pressed={view === "list"} aria-label="List view" onClick={() => setView("list")}><IcList /></button>
              </div>
            </div>
            <div className="days">
              <div className="summary" style={{ marginLeft: 0 }}>
                Showing <b>{filtered.length}</b> of {teams.length} {teams.length === 1 ? "team" : "teams"}{activeDiv ? ` · ${activeDiv.name}` : ""}
                {anyFilter && <button onClick={clear}>Clear filters</button>}
              </div>
              {anyPlayed && <div className="legendwl"><span><b className="fp W">W</b>Won</span><span><b className="fp L">L</b>Lost</span><span><b className="fp T">T</b>Tied</span><span><b className="fp NR">NR</b>No result</span></div>}
            </div>
          </>
        )}
        <div id="list">{body}</div>
      </main>
      <SetDivisionsGroupModal open={showDivModal} onOpenChange={setShowDivModal} onCreate={createDivisions} />
      <UpdateTeamModal
        open={edit.open}
        onOpenChange={(o) => setEdit((p) => ({ ...p, open: o }))}
        teamId={edit.teamId}
        teamSnapshotIdFromList={edit.snapshotId}
        initialData={{ teamName: edit.name }}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: ["tmTeams", tournamentId] });
          void queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getDivisions, tournamentId] });
          if (edit.teamId) void queryClient.invalidateQueries({ queryKey: [QUERY_KEY.teamsSnapShotByTeamId, edit.teamId] });
        }}
      />
    </div>
  );
};

export default TeamsTab;
